import React, { useState, useEffect } from 'react';
import { getUsers } from '../../services/userService';
import { createAppreciation, getSharedAppreciation } from '../../services/appreciationService';
import { getCompanyValues } from '../../services/companyValueService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';

const AppreciationForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [message, setMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showUsersList, setShowUsersList] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [characterCount, setCharacterCount] = useState(0);
  const [error, setError] = useState('');
  const [companyValues, setCompanyValues] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const { currentUser } = useAppContext();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersData, valuesData] = await Promise.all([
          getUsers(),
          getCompanyValues()
        ]);
        setUsers(usersData);
        setCompanyValues(valuesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Load shared appreciation if share token is present
  useEffect(() => {
    const shareToken = searchParams.get('share');
    if (shareToken && users.length > 0) {
      const loadSharedData = async () => {
        try {
          const data = await getSharedAppreciation(shareToken);

          // Map usernames to user objects
          const recipientObjects = users.filter(user =>
            data.recipients.includes(user.username)
          );

          setSelectedRecipients(recipientObjects);
          setMessage(data.message);
          setCharacterCount(data.message.length);
          setSelectedValues(data.company_values || []);
        } catch (error) {
          console.error('Failed to load shared appreciation:', error);
          if (error.response?.status === 410) {
            setError('This shared link has expired.');
          } else if (error.response?.status === 404) {
            setError('Shared link not found.');
          } else {
            setError('Failed to load shared appreciation.');
          }
        }
      };

      loadSharedData();
    }
  }, [searchParams, users]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers([]);
      setShowUsersList(false);
      setHighlightedIndex(-1);
      return;
    }

    const filtered = users.filter(user =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
    setShowUsersList(true);
    setHighlightedIndex(-1);
  }, [searchTerm, users]);

  const handleMessageChange = (e) => {
    const text = e.target.value;
    setMessage(text);
    setCharacterCount(text.length);
  };

  const handleKeyDown = (e) => {
    if (!showUsersList || filteredUsers.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredUsers.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredUsers.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredUsers.length) {
          addRecipient(filteredUsers[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowUsersList(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const addRecipient = (user) => {
    // Check if user is already selected
    if (!selectedRecipients.some(r => r.id === user.id)) {
      setSelectedRecipients([...selectedRecipients, user]);
    }
    setSearchTerm('');
    setShowUsersList(false);
    setHighlightedIndex(-1);
  };

  const removeRecipient = (userId) => {
    setSelectedRecipients(selectedRecipients.filter(user => user.id !== userId));
  };

  const addCompanyValue = (value) => {
    if (!selectedValues.includes(value)) {
      setSelectedValues([...selectedValues, value]);
    }
  };

  const removeCompanyValue = (value) => {
    setSelectedValues(selectedValues.filter(v => v !== value));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (selectedRecipients.length === 0 || !message) {
      setError('Please select at least one recipient and enter a message');
      return;
    }

    try {
      // Send appreciations to all selected recipients
      const senderUsername = currentUser.username;

      const appreciationPromises = selectedRecipients.map(recipient => {
        const appreciationData = {
          sender_username: senderUsername,
          receiver_username: recipient.username,
          message: message,
          company_values: selectedValues
        };

        return createAppreciation(appreciationData);
      });

      await Promise.all(appreciationPromises);

      // Navigate to success page with data
      navigate('/appreciation-success', {
        state: {
          recipients: selectedRecipients,
          message: message,
          companyValues: selectedValues
        }
      });
      
    } catch (error) {
      console.error('Error sending appreciation:', error.response ? error.response.data : error);
      const errorMessage = error.response?.data?.detail || 'Failed to send appreciation. Please try again.';
      setError(errorMessage);
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      <div className="px-6 py-5">
        <h2 className="text-lg font-medium text-gray-900">Send Appreciation</h2>
      </div>
      <div className="px-6 py-5">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded relative" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          <div className="mb-5">
            <label htmlFor="recipients" className="block text-sm font-medium text-gray-700 mb-2">Recipient(s)</label>
            <div className="relative rounded-md shadow-sm">
              <input
                type="text"
                id="recipients"
                className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-4 pr-10 sm:text-sm border-gray-300 rounded-md"
                placeholder="johndoe"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setShowUsersList(true)}
                onKeyDown={handleKeyDown}
              />
            </div>
            
            {/* Selected Recipients */}
            {selectedRecipients.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedRecipients.map(user => (
                  <div 
                    key={user.id} 
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {user.full_name || user.username}
                    <button 
                      type="button" 
                      className="ml-1.5 inline-flex text-blue-400 hover:text-blue-600 focus:outline-none" 
                      onClick={() => removeRecipient(user.id)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Dropdown for user selection */}
            {showUsersList && filteredUsers.length > 0 && (
              <div className="absolute z-10 mt-1 max-w-md bg-white shadow-lg rounded-md py-1 max-h-60 overflow-auto focus:outline-none">
                {filteredUsers.map((user, index) => (
                  <div
                    key={user.id}
                    className={`cursor-pointer select-none relative py-2 pl-3 pr-9 ${
                      index === highlightedIndex ? 'bg-blue-50' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => addRecipient(user)}
                  >
                    {user.username.startsWith('slack_')
                      ? user.full_name
                      : `${user.full_name} (${user.username})`}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mb-5">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
            <textarea
              id="message"
              rows="4"
              className="p-3 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-2 border-gray-300 rounded-md resize-none"
              placeholder="What would you like to recognize them for? Be creative! Use #hashtags for company values."
              value={message}
              onChange={handleMessageChange}
              maxLength={500}
            ></textarea>
            <p className="mt-2 text-xs text-gray-500 flex justify-between">
              <span>Tip: Use #hashtags to tag company values (e.g., #teamwork #innovation)</span>
              <span>{characterCount}/500 characters</span>
            </p>
          </div>

          {/* Company Values */}
          <div className="mb-5">
            <label htmlFor="values" className="block text-sm font-medium text-gray-700 mb-2">
              Company Values (optional)
            </label>

            {/* Selected Values */}
            {selectedValues.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-2">
                {selectedValues.map(value => (
                  <div
                    key={value}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                  >
                    {value}
                    <button
                      type="button"
                      className="ml-1.5 inline-flex text-purple-400 hover:text-purple-600 focus:outline-none"
                      onClick={() => removeCompanyValue(value)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Existing values */}
            {companyValues.length > 0 && (
              <div className="mb-2">
                <p className="text-xs text-gray-500 mb-1">Select from existing values:</p>
                <div className="flex flex-wrap gap-2">
                  {companyValues.map(value => (
                    <button
                      key={value.id}
                      type="button"
                      className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        selectedValues.includes(value.name)
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      onClick={() => selectedValues.includes(value.name) ? removeCompanyValue(value.name) : addCompanyValue(value.name)}
                    >
                      {value.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Send in Secret: Future feature */}

          <div className="flex justify-end">
            {/* Appreciation Link: Future feature */}
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white primary-color hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Send Appreciation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppreciationForm;
